<template>
  <div class="container mt-4">
    <h1 class="mb-4">User Settings</h1>
    
    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    
    <div v-else>
      <div v-if="error" class="alert alert-danger" role="alert">
        {{ error }}
      </div>
      
      <div v-if="successMessage" class="alert alert-success" role="alert">
        {{ successMessage }}
      </div>
      
      <div class="row">
        <div class="col-md-3">
          <div class="card mb-4">
            <div class="card-body text-center">
              <div class="position-relative d-inline-block mb-3">
                <img 
                  :src="profileImagePreview || defaultAvatar" 
                  class="rounded-circle img-fluid" 
                  style="width: 150px; height: 150px; object-fit: cover;"
                  alt="Profile Picture"
                />
                <button 
                  @click="triggerFileInput" 
                  class="btn btn-sm btn-primary position-absolute bottom-0 end-0"
                  style="border-radius: 50%;"
                >
                  <i class="bi bi-pencil"></i>
                </button>
                <input 
                  type="file" 
                  ref="fileInput" 
                  @change="handleFileUpload" 
                  accept="image/*" 
                  style="display: none;"
                />
              </div>
              <h5 class="mb-1">{{ userProfile.displayName || 'Your Name' }}</h5>
              <p class="text-muted">{{ userProfile.skillLevel || 'Skill Level' }}</p>
            </div>
          </div>
          
          <!-- Labelled and marked up as navigation: unlabelled, the buttons
               read as a stack of actions rather than the page's own nav. -->
          <h6 class="settings-nav-label">Settings</h6>
          <nav class="list-group mb-4" aria-label="Settings sections">
            <button 
              class="list-group-item list-group-item-action active"
              @click="activeTab = 'personal'"
              :class="{ active: activeTab === 'personal' }"
            >
              Personal Information
            </button>
            <button 
              class="list-group-item list-group-item-action"
              @click="activeTab = 'preferences'"
              :class="{ active: activeTab === 'preferences' }"
            >
              Designer Preferences
            </button>
            <button 
              class="list-group-item list-group-item-action"
              @click="activeTab = 'feedback'"
              :class="{ active: activeTab === 'feedback' }"
            >
              Feedback Preferences
            </button>
            <button
              class="list-group-item list-group-item-action"
              @click="activeTab = 'invites'"
              :class="{ active: activeTab === 'invites' }"
            >
              Invites
            </button>
            <button
              class="list-group-item list-group-item-action"
              @click="selectApiTab"
              :class="{ active: activeTab === 'api' }"
            >
              MCP &amp; API Access
            </button>
            <button
              class="list-group-item list-group-item-action"
              @click="activeTab = 'feedback-tools'"
              :class="{ active: activeTab === 'feedback-tools' }"
            >
              Feedback
            </button>
          </nav>
        </div>
        
        <div class="col-md-9">
          <div class="card">
            <div class="card-body">
              <!-- Personal Information Tab -->
              <div v-if="activeTab === 'personal'">
                <h3 class="card-title mb-4">Personal Information</h3>
                
                <div class="mb-3">
                  <label for="displayName" class="form-label">Display Name</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    id="displayName" 
                    v-model="userProfile.displayName"
                    placeholder="Enter your display name"
                  >
                </div>
                
                <div class="mb-3">
                  <label for="bio" class="form-label">Short Bio</label>
                  <textarea 
                    class="form-control" 
                    id="bio" 
                    rows="3" 
                    v-model="userProfile.bio"
                    placeholder="Tell us a bit about yourself"
                  ></textarea>
                </div>
                
                <div class="mb-3">
                  <label for="location" class="form-label">Location (City/Country)</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    id="location" 
                    v-model="userProfile.location"
                    placeholder="e.g., New York, USA"
                  >
                </div>
                
                <div class="mb-3">
                  <label for="status" class="form-label">Professional/Hobby Status</label>
                  <select class="form-select" id="status" v-model="userProfile.status">
                    <option value="">Select your status</option>
                    <option value="Professional Designer">Professional Designer</option>
                    <option value="Hobbyist">Hobbyist</option>
                    <option value="Student">Student</option>
                  </select>
                </div>
                
                <div class="mb-3">
                  <label for="website" class="form-label">Website/Portfolio Link</label>
                  <input 
                    type="url" 
                    class="form-control" 
                    id="website" 
                    v-model="userProfile.website"
                    placeholder="https://yourwebsite.com"
                  >
                </div>
                
                <div class="mb-3">
                  <label for="socialMedia" class="form-label">Social Media Handles (Optional)</label>
                  <div class="input-group mb-2">
                    <span class="input-group-text">Twitter</span>
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="userProfile.socialMedia.twitter"
                      placeholder="@username"
                    >
                  </div>
                  <div class="input-group mb-2">
                    <span class="input-group-text">Instagram</span>
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="userProfile.socialMedia.instagram"
                      placeholder="@username"
                    >
                  </div>
                  <div class="input-group">
                    <span class="input-group-text">LinkedIn</span>
                    <input 
                      type="text" 
                      class="form-control" 
                      v-model="userProfile.socialMedia.linkedin"
                      placeholder="profile-url"
                    >
                  </div>
                </div>
              </div>
              
              <!-- Designer Preferences Tab -->
              <div v-if="activeTab === 'preferences'">
                <h3 class="card-title mb-4">Designer Preferences</h3>
                
                <div class="mb-3">
                  <label class="form-label">Preferred Game Genres</label>
                  <div class="row">
                    <div class="col-md-6" v-for="(genre, index) in gameGenres" :key="'genre-'+index">
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          :id="'genre-'+index" 
                          :value="genre"
                          v-model="userProfile.preferences.genres"
                        >
                        <label class="form-check-label" :for="'genre-'+index">
                          {{ genre }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="mb-3">
                  <label class="form-label">Favorite Game Mechanics</label>
                  <div class="row">
                    <div class="col-md-6" v-for="(mechanic, index) in gameMechanics" :key="'mechanic-'+index">
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          :id="'mechanic-'+index" 
                          :value="mechanic"
                          v-model="userProfile.preferences.mechanics"
                        >
                        <label class="form-check-label" :for="'mechanic-'+index">
                          {{ mechanic }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="mb-3">
                  <label for="specialties" class="form-label">Design Specialties</label>
                  <select 
                    class="form-select" 
                    id="specialties" 
                    v-model="userProfile.preferences.specialties" 
                    multiple
                  >
                    <option v-for="(specialty, index) in designSpecialties" :key="index" :value="specialty">
                      {{ specialty }}
                    </option>
                  </select>
                  <div class="form-text">Hold Ctrl (or Cmd) to select multiple options</div>
                </div>
                
                <div class="mb-3">
                  <label class="form-label">Skill Level</label>
                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="skill-beginner" 
                      value="Beginner"
                      v-model="userProfile.skillLevel"
                    >
                    <label class="form-check-label" for="skill-beginner">
                      Beginner
                    </label>
                  </div>
                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="skill-intermediate" 
                      value="Intermediate"
                      v-model="userProfile.skillLevel"
                    >
                    <label class="form-check-label" for="skill-intermediate">
                      Intermediate
                    </label>
                  </div>
                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="skill-advanced" 
                      value="Advanced"
                      v-model="userProfile.skillLevel"
                    >
                    <label class="form-check-label" for="skill-advanced">
                      Advanced
                    </label>
                  </div>
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="skill-professional" 
                      value="Professional"
                      v-model="userProfile.skillLevel"
                    >
                    <label class="form-check-label" for="skill-professional">
                      Professional
                    </label>
                  </div>
                </div>
              </div>
              
              <!-- Feedback Preferences Tab -->
              <div v-if="activeTab === 'feedback'">
                <h3 class="card-title mb-4">Feedback Preferences</h3>
                
                <div class="mb-3">
                  <label class="form-label">Feedback Frequency</label>
                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="feedback-frequent" 
                      value="Frequent"
                      v-model="userProfile.feedback.frequency"
                    >
                    <label class="form-check-label" for="feedback-frequent">
                      Frequent (I want regular feedback on my designs)
                    </label>
                  </div>
                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="feedback-occasional" 
                      value="Occasional"
                      v-model="userProfile.feedback.frequency"
                    >
                    <label class="form-check-label" for="feedback-occasional">
                      Occasional (I want feedback at key milestones)
                    </label>
                  </div>
                  <div class="form-check mb-2">
                    <input 
                      class="form-check-input" 
                      type="radio" 
                      id="feedback-minimal" 
                      value="Minimal"
                      v-model="userProfile.feedback.frequency"
                    >
                    <label class="form-check-label" for="feedback-minimal">
                      Minimal (I prefer to work independently)
                    </label>
                  </div>
                </div>
                
                <div class="mb-3">
                  <label class="form-label">Feedback Focus Areas</label>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          id="focus-mechanics" 
                          value="Game Mechanics"
                          v-model="userProfile.feedback.focusAreas"
                        >
                        <label class="form-check-label" for="focus-mechanics">
                          Game Mechanics
                        </label>
                      </div>
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          id="focus-balance" 
                          value="Game Balance"
                          v-model="userProfile.feedback.focusAreas"
                        >
                        <label class="form-check-label" for="focus-balance">
                          Game Balance
                        </label>
                      </div>
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          id="focus-rules" 
                          value="Rules Clarity"
                          v-model="userProfile.feedback.focusAreas"
                        >
                        <label class="form-check-label" for="focus-rules">
                          Rules Clarity
                        </label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          id="focus-visuals" 
                          value="Visual Design"
                          v-model="userProfile.feedback.focusAreas"
                        >
                        <label class="form-check-label" for="focus-visuals">
                          Visual Design
                        </label>
                      </div>
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          id="focus-player" 
                          value="Player Experience"
                          v-model="userProfile.feedback.focusAreas"
                        >
                        <label class="form-check-label" for="focus-player">
                          Player Experience
                        </label>
                      </div>
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          id="focus-marketability" 
                          value="Marketability"
                          v-model="userProfile.feedback.focusAreas"
                        >
                        <label class="form-check-label" for="focus-marketability">
                          Marketability
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="mb-3">
                  <label class="form-label">Feedback Style Preference</label>
                  <select class="form-select" v-model="userProfile.feedback.style">
                    <option value="">Select your preferred feedback style</option>
                    <option value="Direct">Direct (straightforward critique)</option>
                    <option value="Constructive">Constructive (balanced positive and negative)</option>
                    <option value="Gentle">Gentle (emphasize positives)</option>
                    <option value="Detailed">Detailed (comprehensive analysis)</option>
                  </select>
                </div>
                
                <div class="mb-3">
                  <label for="feedbackNotes" class="form-label">Additional Feedback Notes</label>
                  <textarea 
                    class="form-control" 
                    id="feedbackNotes" 
                    rows="3" 
                    v-model="userProfile.feedback.notes"
                    placeholder="Any specific instructions for people providing feedback on your designs"
                  ></textarea>
                </div>
                
                <div class="mb-3">
                  <div class="form-check">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="allowPublicFeedback" 
                      v-model="userProfile.feedback.allowPublic"
                    >
                    <label class="form-check-label" for="allowPublicFeedback">
                      Allow public feedback on my designs
                    </label>
                  </div>
                  <div class="form-text">When disabled, only invited collaborators can provide feedback</div>
                </div>
              </div>

              <!-- Invites Tab -->
              <div v-if="activeTab === 'invites'">
                <h3 class="card-title mb-4">Board Game Invites</h3>
                
                <div v-if="loadingInvites" class="text-center">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading invites...</span>
                  </div>
                </div>

                <div v-else-if="invites.length === 0" class="text-center text-muted py-5">
                  <i class="fas fa-inbox fa-3x mb-3"></i>
                  <p>You don't have any pending invites.</p>
                </div>

                <div v-else class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>Board Game</th>
                        <th>From</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="invite in invites" :key="invite.id">
                        <td>
                          <router-link 
                            :to="{ name: 'BoardGameDetail', params: { id: invite.boardGame.id }}"
                            class="text-decoration-none"
                          >
                            {{ invite.boardGame.name }}
                          </router-link>
                        </td>
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="user-icon bg-primary text-white me-2 rounded-circle p-2">
                              <i class="fas fa-user"></i>
                            </div>
                            <div>
                              <div>{{ invite.boardGame.ownerName }}</div>
                              <small class="text-muted">{{ invite.boardGame.ownerEmail }}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span class="badge bg-warning text-dark">
                            {{ invite.status.charAt(0).toUpperCase() + invite.status.slice(1) }}
                          </span>
                        </td>
                        <td>
                          <div class="btn-group">
                            <button 
                              class="btn btn-sm btn-success" 
                              @click="handleInviteAction(invite.id, 'accepted')"
                              :disabled="processingInvite === invite.id"
                            >
                              <span v-if="processingInvite === invite.id" class="spinner-border spinner-border-sm" role="status"></span>
                              <span v-else>Accept</span>
                            </button>
                            <button 
                              class="btn btn-sm btn-danger" 
                              @click="handleInviteAction(invite.id, 'rejected')"
                              :disabled="processingInvite === invite.id"
                            >
                              <span v-if="processingInvite === invite.id" class="spinner-border spinner-border-sm" role="status"></span>
                              <span v-else>Reject</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- MCP & API Access Tab -->
              <div v-if="activeTab === 'api'">
                <h3 class="card-title mb-1">MCP &amp; API Access</h3>
                <p class="text-muted">
                  Designer Keys let tools like Claude read and build your games
                  through the Sparkforge API. A key acts as you — treat it like a
                  password.
                </p>

                <div v-if="apiUnreachable" class="alert alert-warning">
                  <strong>The Sparkforge API isn't reachable.</strong>
                  <div class="small mt-1">{{ apiError }}</div>
                </div>
                <div v-else-if="apiError" class="alert alert-danger">{{ apiError }}</div>

                <!-- Shown once, immediately after minting -->
                <div v-if="newKey" class="alert alert-success">
                  <h5 class="alert-heading">Key created — copy it now</h5>
                  <p class="mb-2 small">
                    This is the only time it will be shown. It is stored hashed and
                    cannot be recovered.
                  </p>
                  <div class="input-group mb-3">
                    <input type="text" class="form-control font-monospace" :value="newKey.token" readonly />
                    <button class="btn btn-outline-dark" @click="copy(newKey.token, 'token')">
                      {{ copied === 'token' ? 'Copied' : 'Copy' }}
                    </button>
                  </div>
                  <p class="mb-1 small fw-bold">Paste this into your MCP client config:</p>
                  <pre class="bg-dark text-light p-3 rounded small mb-2"><code>{{ newKey.snippet }}</code></pre>
                  <div class="d-flex gap-2">
                    <button class="btn btn-sm btn-outline-dark" @click="copy(newKey.snippet, 'snippet')">
                      {{ copied === 'snippet' ? 'Copied' : 'Copy config' }}
                    </button>
                    <button class="btn btn-sm btn-secondary" @click="newKey = null">
                      I've saved it
                    </button>
                  </div>
                  <hr />
                  <p class="mb-0 small">
                    Claude Desktop:
                    <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>,
                    then restart it. Claude Code: <code>.mcp.json</code> in the repo root.
                  </p>
                </div>

                <!-- Create -->
                <div class="card mb-4">
                  <div class="card-body">
                    <h5 class="card-title">Create a Designer Key</h5>
                    <div class="row g-3">
                      <div class="col-md-6">
                        <label class="form-label">What is this key for?</label>
                        <input
                          v-model="keyForm.name"
                          type="text"
                          class="form-control"
                          placeholder="Claude Desktop — laptop"
                        />
                      </div>
                      <div class="col-md-3">
                        <label class="form-label">Expires</label>
                        <select v-model="keyForm.expiresDays" class="form-select">
                          <option :value="30">30 days</option>
                          <option :value="90">90 days</option>
                          <option :value="365">1 year</option>
                          <option :value="null">Never</option>
                        </select>
                      </div>
                    </div>

                    <label class="form-label mt-3">Permissions</label>
                    <div class="row">
                      <div v-for="scope in availableScopes" :key="scope" class="col-md-4">
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`scope-${scope}`"
                            :value="scope"
                            v-model="keyForm.scopes"
                          />
                          <label class="form-check-label small" :for="`scope-${scope}`">
                            <code>{{ scope }}</code>
                            <span class="text-muted d-block">{{ scopeHelp[scope] }}</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    <button
                      class="btn btn-primary mt-3"
                      @click="createKey"
                      :disabled="creatingKey || !keyForm.name || !keyForm.scopes.length"
                    >
                      <span v-if="creatingKey" class="spinner-border spinner-border-sm me-2"></span>
                      Create Key
                    </button>
                  </div>
                </div>

                <!-- Existing -->
                <h5>Your keys</h5>
                <div v-if="loadingKeys" class="text-center py-3">
                  <div class="spinner-border" role="status"></div>
                </div>
                <div v-else-if="!apiKeys.length" class="text-muted py-3">
                  No keys yet.
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Name</th><th>Key</th><th>Scopes</th>
                        <th>Last used</th><th>Expires</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="k in apiKeys" :key="k.id" :class="{ 'text-muted': k.revokedAt }">
                        <td>{{ k.name }}</td>
                        <td><code class="small">{{ k.prefix }}…</code></td>
                        <td class="small">{{ (k.scopes || []).length }} scopes</td>
                        <td class="small">{{ formatDate(k.lastUsedAt) || 'never' }}</td>
                        <td class="small">{{ formatDate(k.expiresAt) || 'never' }}</td>
                        <td class="text-end">
                          <span v-if="k.revokedAt" class="badge bg-secondary">revoked</span>
                          <button
                            v-else
                            class="btn btn-sm btn-outline-danger"
                            @click="revokeKey(k)"
                            :disabled="revoking === k.id"
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="alert alert-light border mt-3 small">
                  <strong>Your account:</strong>
                  <code>{{ apiIdentity.email || '—' }}</code>
                  &nbsp;·&nbsp; <strong>UID:</strong> <code>{{ apiIdentity.userId || '—' }}</code>
                  &nbsp;·&nbsp; <strong>Data store:</strong>
                  <code>{{ apiIdentity.store || '—' }}</code>
                  <div v-if="apiIdentity.store === 'local'" class="text-danger mt-1">
                    The API is running against a local dev store, so it will not see
                    your real games. Start it with <code>SPARKFORGE_STORE=firestore</code>.
                  </div>
                </div>
              </div>

              <!-- In-app feedback (FoldBack) -->
              <div v-if="activeTab === 'feedback-tools'">
                <h3 class="card-title mb-1">Feedback</h3>
                <p class="text-muted">
                  Turn this on and a feedback tool appears on every page. Click any
                  element, say what should change, and it goes to the review queue
                  with the page, the element and the build it came from.
                </p>

                <div v-if="!foldbackConfigured" class="alert alert-warning py-2 small">
                  Feedback isn't configured in this build, so the toggle would do
                  nothing. It needs the FoldBack environment variables at build time.
                </div>
                <div v-else-if="foldbackStatus && !foldbackStatus.ok"
                     class="alert alert-warning py-2 small">
                  <strong>The tool won't appear yet.</strong>
                  <template v-if="foldbackStatus.reason === 'disabled-on-project'">
                    FoldBack has in-app feedback switched off for this project, so
                    the widget loads and then renders nothing. Turn it on in the
                    FoldBack dashboard under Project Settings → In-app Feedback.
                  </template>
                  <template v-else-if="foldbackStatus.reason === 'unreachable'">
                    Couldn't reach FoldBack{{ foldbackStatus.status ? ` (HTTP ${foldbackStatus.status})` : '' }}.
                    On a local build, the FoldBack server may not be running.
                  </template>
                  <template v-else>Feedback is unavailable ({{ foldbackStatus.reason }}).</template>
                </div>
                <div v-else-if="foldbackStatus && foldbackStatus.ok"
                     class="alert alert-success py-2 small">
                  Connected to FoldBack — feedback is live for this project.
                </div>

                <div class="card">
                  <div class="card-body d-flex align-items-start gap-3">
                    <div class="form-check form-switch pt-1">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="foldbackToggle"
                        :disabled="!foldbackConfigured"
                        v-model="foldbackEnabled"
                        @change="onFoldbackToggle"
                      />
                    </div>
                    <div class="flex-grow-1">
                      <label class="form-check-label fw-semibold d-block" for="foldbackToggle">
                        Show the feedback tool on every page
                      </label>
                      <div class="small text-muted">
                        Saved in this browser only — it does not change what anyone
                        else sees. Turning it on loads the tool immediately; turning
                        it off takes effect on the next page load.
                      </div>
                      <div v-if="foldbackJustEnabled" class="small text-success mt-2">
                        On. Open any page and the feedback tool will be there.
                      </div>
                    </div>
                  </div>
                </div>

                <div class="alert alert-light border mt-3 small">
                  <strong>One-off, without switching it on:</strong> add
                  <code>?foldback=enabled</code> to any page URL. Note that navigating
                  inside the app drops the query string, and the tool goes with it —
                  which is why the toggle exists.
                </div>
              </div>

              <div v-if="activeTab !== 'api' && activeTab !== 'feedback-tools'" class="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  class="btn btn-secondary me-2"
                  @click="resetForm"
                >
                  Reset
                </button>
                <button 
                  type="button" 
                  class="btn btn-primary"
                  @click="saveUserProfile"
                  :disabled="loading"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { auth, firestore, storage, getUserInvites, updateEditorStatus } from '@/firebase';
import { sparkforgeApi, mcpConfigSnippet } from '@/sparkforgeApi';
import { isFeedbackEnabled, setFeedbackEnabled, isConfigured as foldbackIsConfigured, checkFoldbackStatus } from '@/foldback';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';

export default {
  name: 'UserSettingsPage',
  setup() {
    const loading = ref(false);
    const error = ref('');
    const successMessage = ref('');
    const activeTab = ref('personal');
    const fileInput = ref(null);
    const profileImagePreview = ref(null);
    // Inline SVG rather than via.placeholder.com, which is dead and shows a
    // broken image where a profile picture should be.
    const defaultAvatar =
      'data:image/svg+xml;charset=utf-8,' +
      encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150">' +
          '<rect width="150" height="150" fill="#eee8dd"/>' +
          '<circle cx="75" cy="60" r="26" fill="#c9c0b2"/>' +
          '<ellipse cx="75" cy="126" rx="44" ry="30" fill="#c9c0b2"/>' +
        '</svg>'
      );
    const selectedFile = ref(null);

    // Invites data
    const loadingInvites = ref(false);
    const invites = ref([]);
    const processingInvite = ref(null);

    // --- MCP & API access -------------------------------------------------
    const apiKeys = ref([]);
    const loadingKeys = ref(false);
    const creatingKey = ref(false);
    const revoking = ref(null);
    const apiError = ref('');
    const apiUnreachable = ref(false);
    const copied = ref('');
    const newKey = ref(null);
    const apiIdentity = ref({});
    const availableScopes = ref([
      'games:read', 'games:write', 'cards:write',
      'boards:write', 'assets:write', 'exports:read', 'playtest:write'
    ]);
    const scopeHelp = {
      'games:read': 'Read your games',
      'games:write': 'Create and edit games and rules',
      'cards:write': 'Create and edit cards',
      'boards:write': 'Create and edit boards',
      'assets:write': 'Upload and generate art',
      'exports:read': 'Generate print PDFs',
      'playtest:write': 'Run AI playtest reviews (costs credits)'
    };
    // --- in-app feedback (FoldBack) ---
    const foldbackEnabled = ref(isFeedbackEnabled());
    const foldbackConfigured = ref(foldbackIsConfigured());
    const foldbackJustEnabled = ref(false);
    const foldbackStatus = ref(null);
    const onFoldbackToggle = () => {
      setFeedbackEnabled(foldbackEnabled.value);
      foldbackJustEnabled.value = foldbackEnabled.value;
    };
    // Ask FoldBack whether feedback is actually on for this project. The SDK
    // can load fine and still show nothing, and without this the toggle looks
    // broken with no way to tell why.
    checkFoldbackStatus().then((s) => { foldbackStatus.value = s; });

    const keyForm = reactive({
      name: '',
      expiresDays: 90,
      // playtest is off by default because it costs money per call.
      scopes: availableScopes.value.filter((s) => s !== 'playtest:write')
    });

    const formatDate = (value) => {
      if (!value) return '';
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
    };

    const handleApiError = (err) => {
      apiUnreachable.value = err.code === 'unreachable';
      apiError.value = err.message;
    };

    const loadApiKeys = async () => {
      loadingKeys.value = true;
      apiError.value = '';
      apiUnreachable.value = false;
      try {
        const [keys, me, scopes] = await Promise.all([
          sparkforgeApi.listKeys(),
          sparkforgeApi.me(),
          sparkforgeApi.scopes().catch(() => null)
        ]);
        apiKeys.value = keys.data || [];
        apiIdentity.value = me || {};
        if (scopes && scopes.scopes) availableScopes.value = scopes.scopes;
      } catch (err) {
        handleApiError(err);
      } finally {
        loadingKeys.value = false;
      }
    };

    const selectApiTab = () => {
      activeTab.value = 'api';
      loadApiKeys();
    };

    const createKey = async () => {
      creatingKey.value = true;
      apiError.value = '';
      try {
        const result = await sparkforgeApi.createKey({
          name: keyForm.name,
          scopes: [...keyForm.scopes],
          expiresDays: keyForm.expiresDays
        });
        // Shown exactly once — the server never returns it again.
        newKey.value = {
          token: result.token,
          snippet: mcpConfigSnippet(result.token)
        };
        keyForm.name = '';
        await loadApiKeys();
      } catch (err) {
        handleApiError(err);
      } finally {
        creatingKey.value = false;
      }
    };

    const revokeKey = async (key) => {
      if (!confirm(`Revoke "${key.name}"? Any tool using it stops working immediately.`)) {
        return;
      }
      revoking.value = key.id;
      try {
        await sparkforgeApi.revokeKey(key.id);
        await loadApiKeys();
      } catch (err) {
        handleApiError(err);
      } finally {
        revoking.value = null;
      }
    };

    const copy = async (text, what) => {
      try {
        await navigator.clipboard.writeText(text);
        copied.value = what;
        setTimeout(() => { copied.value = ''; }, 2000);
      } catch (e) {
        apiError.value = 'Could not copy to clipboard — select the text manually.';
      }
    };
    
    // Game genres, mechanics, and specialties
    const gameGenres = [
      'Strategy', 'Euro Games', 'Ameritrash', 'Party Games', 'Card Games', 
      'Cooperative Games', 'Deck Building', 'Worker Placement', 'Role-Playing Games',
      'Wargames', 'Abstract Games', 'Family Games', 'Children\'s Games'
    ];
    
    const gameMechanics = [
      'Dice Rolling', 'Card Drafting', 'Worker Placement', 'Area Control', 
      'Deck Building', 'Tile Placement', 'Resource Management', 'Hand Management',
      'Set Collection', 'Auction/Bidding', 'Cooperative Play', 'Variable Player Powers'
    ];
    
    const designSpecialties = [
      'Card Games', 'Board Games', 'Miniatures Games', 'Role-Playing Games',
      'Party Games', 'Strategy Games', 'Game Art', 'Game Rules Writing',
      'Game Balance', 'Game Testing', 'Game Production'
    ];
    
    // Default user profile structure
    const defaultUserProfile = {
      displayName: '',
      bio: '',
      location: '',
      status: '',
      website: '',
      socialMedia: {
        twitter: '',
        instagram: '',
        linkedin: ''
      },
      preferences: {
        genres: [],
        mechanics: [],
        specialties: []
      },
      feedback: {
        frequency: '',
        focusAreas: [],
        style: '',
        notes: '',
        allowPublic: true
      },
      skillLevel: ''
    };
    
    const userProfile = reactive({...defaultUserProfile});
    
    // Load user profile data
    const loadUserProfile = async () => {
      if (!auth.currentUser) return;
      
      loading.value = true;
      error.value = '';
      
      try {
        // Get user profile from Firestore
        const userDocRef = doc(firestore, 'userProfiles', auth.currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          // Merge existing data with default structure to ensure all fields exist
          const userData = userDoc.data();
          
          // Make sure socialMedia, preferences, and feedback objects exist
          if (!userData.socialMedia) userData.socialMedia = {};
          if (!userData.preferences) userData.preferences = {};
          if (!userData.preferences.genres) userData.preferences.genres = [];
          if (!userData.preferences.mechanics) userData.preferences.mechanics = [];
          if (!userData.preferences.specialties) userData.preferences.specialties = [];
          
          // Initialize feedback object if it doesn't exist
          if (!userData.feedback) userData.feedback = {};
          if (!userData.feedback.focusAreas) userData.feedback.focusAreas = [];
          
          // Update userProfile with data from Firestore
          Object.keys(defaultUserProfile).forEach(key => {
            if (key === 'socialMedia' || key === 'preferences' || key === 'feedback') {
              userProfile[key] = { ...defaultUserProfile[key], ...userData[key] };
            } else {
              userProfile[key] = userData[key] || defaultUserProfile[key];
            }
          });
        }
        
        // Set display name from auth if not in profile
        if (!userProfile.displayName && auth.currentUser.displayName) {
          userProfile.displayName = auth.currentUser.displayName;
        }
        
        // Set profile image preview
        if (auth.currentUser.photoURL) {
          profileImagePreview.value = auth.currentUser.photoURL;
        }
        
      } catch (err) {
        console.error('Error loading user profile:', err);
        error.value = 'Failed to load user profile. Please try again.';
      } finally {
        loading.value = false;
      }
    };
    
    // Save user profile data
    const saveUserProfile = async () => {
      if (!auth.currentUser) return;
      
      loading.value = true;
      error.value = '';
      successMessage.value = '';
      
      try {
        const userDocRef = doc(firestore, 'userProfiles', auth.currentUser.uid);
        
        // Upload profile image if selected
        if (selectedFile.value) {
          const imageRef = storageRef(storage, `profileImages/${auth.currentUser.uid}`);
          await uploadBytes(imageRef, selectedFile.value);
          const downloadURL = await getDownloadURL(imageRef);
          
          // Update auth profile
          await updateProfile(auth.currentUser, {
            photoURL: downloadURL
          });
          
          // Update local preview
          profileImagePreview.value = downloadURL;
        }
        
        // Update display name in auth profile if changed
        if (userProfile.displayName !== auth.currentUser.displayName) {
          await updateProfile(auth.currentUser, {
            displayName: userProfile.displayName
          });
        }
        
        // Save profile data to Firestore
        await setDoc(userDocRef, {
          ...userProfile,
          updatedAt: new Date()
        }, { merge: true });
        
        successMessage.value = 'Profile updated successfully!';
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          successMessage.value = '';
        }, 3000);
        
      } catch (err) {
        console.error('Error saving user profile:', err);
        error.value = 'Failed to save profile. Please try again.';
      } finally {
        loading.value = false;
        selectedFile.value = null;
      }
    };
    
    // Reset form to last saved values
    const resetForm = () => {
      loadUserProfile();
    };
    
    // Trigger file input click
    const triggerFileInput = () => {
      fileInput.value.click();
    };
    
    // Handle file upload
    const handleFileUpload = (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      // Check file type
      if (!file.type.match('image.*')) {
        error.value = 'Please select an image file';
        return;
      }
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        error.value = 'Image size should be less than 5MB';
        return;
      }
      
      selectedFile.value = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        profileImagePreview.value = e.target.result;
      };
      reader.readAsDataURL(file);
    };
    
    // Load user profile on component mount
    onMounted(() => {
      if (auth.currentUser) {
        loadUserProfile();
      }
    });
    
    // Load invites
    const loadInvites = async () => {
      try {
        loadingInvites.value = true;
        const userEmail = auth.currentUser?.email;
        if (userEmail) {
          const userInvites = await getUserInvites(userEmail);
          invites.value = userInvites;
        }
      } catch (err) {
        console.error('Error loading invites:', err);
        error.value = 'Failed to load invites. Please try again.';
      } finally {
        loadingInvites.value = false;
      }
    };

    // Handle invite actions (accept/reject)
    const handleInviteAction = async (inviteId, status) => {
      try {
        processingInvite.value = inviteId;
        await updateEditorStatus(inviteId, status);
        
        // Remove the invite from the list
        invites.value = invites.value.filter(invite => invite.id !== inviteId);
        
        successMessage.value = `Successfully ${status} the invitation.`;
        setTimeout(() => {
          successMessage.value = '';
        }, 3000);
      } catch (err) {
        console.error('Error updating invite status:', err);
        error.value = 'Failed to update invite status. Please try again.';
      } finally {
        processingInvite.value = null;
      }
    };

    // Load invites when component mounts
    onMounted(() => {
      if (auth.currentUser) {
        loadUserProfile();
        loadInvites();
      }
    });

    return {
      loading,
      error,
      successMessage,
      activeTab,
      userProfile,
      fileInput,
      profileImagePreview,
      defaultAvatar,
      gameGenres,
      gameMechanics,
      designSpecialties,
      saveUserProfile,
      resetForm,
      triggerFileInput,
      handleFileUpload,
      // Invites functionality
      loadingInvites,
      invites,
      processingInvite,
      handleInviteAction,
      // MCP & API access
      apiKeys,
      loadingKeys,
      creatingKey,
      revoking,
      apiError,
      apiUnreachable,
      copied,
      newKey,
      apiIdentity,
      availableScopes,
      scopeHelp,
      keyForm,
      formatDate,
      foldbackEnabled,
      foldbackConfigured,
      foldbackJustEnabled,
      foldbackStatus,
      onFoldbackToggle,
      selectApiTab,
      loadApiKeys,
      createKey,
      revokeKey,
      copy
    };
  }
};
</script>

<style scoped>
.list-group-item.active {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.settings-nav-label {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.72rem;
  color: var(--ink-light, #8a8a8a);
  margin-bottom: 0.4rem;
  padding-left: 0.15rem;
}
</style>